import React from 'react';
import {
  IconButton,
  Tooltip,
  Box,
} from '@mui/material';
import {
  VolumeUp as VolumeUpIcon,
  Stop as StopIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  VolumeOff as VolumeOffIcon,
} from '@mui/icons-material';
import { useTextToSpeech } from '../../hooks/useTextToSpeech';
import { DESIGN_TOKENS } from '../../design-system/tokens/colors';

interface SpeechButtonProps {
  text: string;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
}

export const SpeechButton: React.FC<SpeechButtonProps> = ({
  text,
  size = 'small',
  disabled = false
}) => {
  const { speak, stop, pause, resume, isSpeaking, isPaused, isSupported } = useTextToSpeech({
    lang: 'pt-BR',
    rate: 0.85, // Velocidade um pouco mais rápida, mas ainda clara
    pitch: 0.8, // Tom mais grave para teste
    volume: 1
  });

  // Se não há suporte ou texto, não renderiza o botão
  if (!isSupported || !text.trim()) {
    return null;
  }

  const handleClick = () => {
    if (disabled) return;

    if (isSpeaking && !isPaused) {
      // Se está falando, pausa
      pause();
    } else if (isPaused) {
      // Se está pausado, retoma
      resume();
    } else {
      // Se não está falando, inicia
      speak(text);
    }
  };

  const handleStop = (e: React.MouseEvent) => {
    e.stopPropagation();
    stop();
  };

  const getIcon = () => {
    if (!isSpeaking) {
      return <VolumeUpIcon />;
    }
    if (isPaused) {
      return <PlayArrowIcon />;
    }
    return <PauseIcon />;
  };

  const getTooltip = () => {
    if (!isSpeaking) {
      return 'Ouvir mensagem';
    }
    if (isPaused) {
      return 'Continuar reprodução';
    }
    return 'Pausar reprodução';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={getTooltip()} arrow>
        <IconButton
          onClick={handleClick}
          disabled={disabled}
          size={size}
          sx={{
            color: isSpeaking 
              ? DESIGN_TOKENS.colors.primary[600] 
              : DESIGN_TOKENS.colors.neutral[600],
            '&:hover': {
              backgroundColor: DESIGN_TOKENS.colors.primary[50],
              color: DESIGN_TOKENS.colors.primary[700],
            },
            '&:disabled': {
              color: DESIGN_TOKENS.colors.neutral[400],
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {getIcon()}
        </IconButton>
      </Tooltip>
      
      {/* Botão de parar (só aparece quando está falando) */}
      {isSpeaking && (
        <Tooltip title="Parar reprodução" arrow>
          <IconButton
            onClick={handleStop}
            disabled={disabled}
            size={size}
            sx={{
              color: DESIGN_TOKENS.colors.neutral[600],
              '&:hover': {
                backgroundColor: DESIGN_TOKENS.colors.neutral[100],
                color: DESIGN_TOKENS.colors.neutral[800],
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <StopIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default SpeechButton;